// Why a leave balance is the number it is.
//
// leave_balances holds two running scalars and no history, so "where did my
// 8.5 casual days come from" had no answer. The ledger (20260908100000) records
// every movement with its reason, the period it belongs to, and the policy that
// was in force at the time. This reads it.
//
// The read is gated by ll_read — profile_id = auth.uid() or people:full — so a
// person sees their own and HR sees everyone's. Unlike audit_log, which is
// org:full/people:full and therefore excludes the very person whose balance moved.
import { supabase } from './supabase';
import { monthLabel } from './payrollPeriods';

/** What moved a balance. `adjustment` is the honest fallback for a write that
 *  stated no intent — an HR PATCH straight at the table, or a seed. */
export type LedgerSource =
  'entitlement' | 'accrual' | 'taken' | 'returned' | 'settlement' | 'adjustment';

export type LedgerRow = {
  id: string;
  leave_type_id: string;
  /** allotted and used are different facts; a trail that merged them could not
   *  be reconciled against the table it describes. */
  field: 'allotted' | 'used';
  delta: number;
  effective_on: string;
  period_year: number;
  period_month: number;
  source: LedgerSource;
  request_id: string | null;
  accrual_date: string | null;
  settlement_id: string | null;
  actor: string | null;
  /** The inputs in force when this moved — the accrual rate, the caps, what a
   *  cap took. org_settings keeps no history, so without this a past month
   *  could never be re-derived. */
  policy: Record<string, unknown>;
  note: string | null;
  created_at: string;
};

const SELECT =
  'id, leave_type_id, field, delta, effective_on, period_year, period_month, ' +
  'source, request_id, accrual_date, settlement_id, actor, policy, note, created_at';

/** One person's movements, newest first. Ordered by the period it BELONGS to,
 *  not when it was written: leave starting 31 Aug files under August however
 *  late it was approved. */
export async function loadLedger(profileId: string): Promise<LedgerRow[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('leave_ledger')
    .select(SELECT)
    .eq('profile_id', profileId)
    .order('period_year', { ascending: false })
    .order('period_month', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(500);
  if (error) return [];
  return (data ?? []) as unknown as LedgerRow[];
}

/**
 * The date the record honestly begins.
 *
 * There is no backfill — months before this were never captured, and rendering
 * them as an empty table would read as "nothing happened" rather than "we do
 * not know". Floored server-side at the deploy date so one back-dated
 * adjustment cannot drag it earlier and claim coverage the ledger lacks.
 */
export async function ledgerSince(): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('leave_ledger_since');
  if (error) return null;
  return (data as string) ?? null;
}

// The notes the comp-off rule changes wrote on balances (20261008110000,
// 20261010120000, 20261019100000, 20261022100000).
const COMP_OFF_NOTE =
  /^(Comp off |Overtime is now credited once a month|Capped at the org standard|A .+ day charged again for a leave that was then withdrawn)/;

const effect = (r: LedgerRow) => (r.field === 'used' ? -r.delta : r.delta);

/**
 * Comp off never changes a balance (owner, 2026-10-22), but the rules before
 * that did: credits went onto Casual, were taken back, capped, given back as
 * used days and charged again — rows that cancel out and read as noise (owner:
 * "clear unwanted leave trails"). Every overtime row and every comp-off rule
 * adjustment of a type folds into ONE line, dropped when it nets to zero, so
 * the trail shows only real movements and still adds up to the balance. A
 * non-zero remainder (an old credit still in the balance) is one line at the
 * latest of them. The rows themselves stay in the database.
 */
export const foldCompOff = (rows: readonly LedgerRow[]): LedgerRow[] => {
  const compOff = (r: LedgerRow) =>
    r.source === 'accrual' || (r.source === 'adjustment' && !!r.note && COMP_OFF_NOTE.test(r.note));
  const net = new Map<string, { sum: number; last: LedgerRow }>();
  for (const r of rows) {
    if (!compOff(r)) continue;
    const t = net.get(r.leave_type_id);
    const later = !t || r.effective_on > t.last.effective_on;
    net.set(r.leave_type_id, {
      sum: (t?.sum ?? 0) + effect(r),
      last: later ? r : t.last,
    });
  }
  const out: LedgerRow[] = [];
  for (const r of rows) {
    if (!compOff(r)) {
      out.push(r);
      continue;
    }
    const t = net.get(r.leave_type_id);
    if (!t || t.last !== r || Math.abs(t.sum) < 1e-9) continue;
    out.push({
      ...r,
      id: `comp-off-${r.leave_type_id}`,
      field: 'allotted',
      delta: Math.round(t.sum * 100) / 100,
      source: 'adjustment',
      request_id: null,
      policy: {},
      note: 'Overtime credits, net of the comp off rule changes up to 22 Oct 2026',
    });
  }
  return out;
};

/** Groups movements into the periods they belong to, newest first. */
export const byPeriod = (rows: readonly LedgerRow[]) => {
  const out = new Map<string, LedgerRow[]>();
  for (const r of rows) {
    const key = `${r.period_year}-${String(r.period_month).padStart(2, '0')}`;
    const bucket = out.get(key);
    if (bucket) bucket.push(r);
    else out.set(key, [r]);
  }
  return [...out.entries()];
};

/**
 * The one-line explanation of a movement, built from what was recorded rather
 * than re-derived — which is the whole point of storing the policy alongside.
 */
export const explain = (r: LedgerRow): string => {
  const p = r.policy ?? {};
  const num = (k: string) => (typeof p[k] === 'number' ? (p[k] as number) : null);

  switch (r.source) {
    case 'accrual': {
      const earned = num('earned');
      const granted = num('granted');
      const lost = num('lostToCap');
      const rate = num('hoursPerLeave');
      const hours = num('otHours');
      // A month settled at payroll lock (20261008110000) names its month and
      // credits in half days; older rows are one day's per-day accrual.
      const month = typeof p.settledMonth === 'string' ? p.settledMonth : null;
      const base =
        hours != null && rate != null
          ? month
            ? `${monthLabel(Number(month.slice(0, 4)), Number(month.slice(5, 7)))} overtime: ` +
              `${hours}h ÷ ${rate / 2}h per half day`
            : `${hours}h overtime ÷ ${rate}h per day`
          : 'overtime accrual';
      // History only: from 20261019100000 (#261) until 20261022100000 comp off
      // gave back used days, so a −used row cancelled days and a +used row was a
      // lower re-settle charging them. Comp off writes no row now; the days it
      // gave back were charged again by one 'adjustment' row, told by its note.
      if (r.field === 'used') {
        const n = Math.abs(r.delta);
        const d = `${n} ${n === 1 ? 'day' : 'days'}`;
        return r.delta < 0 ? `${base} — cancels ${d} already used` : `${base} — ${d} charged again`;
      }
      // The number that used to be computed and thrown away on every accrual.
      return lost != null && lost > 0
        ? `${base} — earned ${earned}, granted ${granted}, ${lost} lost to the monthly cap`
        : base;
    }
    case 'settlement': {
      const rate = num('rate');
      return rate != null
        ? `Shortfall settled in leave at ₹${rate.toFixed(2)}/day`
        : 'Shortfall settled in leave';
    }
    case 'entitlement':
      return 'Annual entitlement';
    case 'taken':
      return 'Leave taken';
    case 'returned':
      return 'Returned — worked on a leave day, or the request was reverted';
    case 'adjustment':
      return r.note ?? 'Adjusted by HR, with no reason recorded';
  }
};
